import React from 'react';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES, COMMUNITY_ROLES_LABELS } from '@/constants/community-roles';
import { UNIT_ROLES, UNIT_ROLES_LABELS, UNIT_ROLES_DESCRIPTIONS } from '@/constants/unit-roles';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Theme,
  Typography,
  useTheme,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@mui/material';
import {
  Clock as ClockIcon,
  DoorOpen as DoorOpenIcon,
  House as HouseIcon,
  Laptop,
  PencilSimple as PencilSimpleIcon,
  PlusCircle as PlusCircleIcon,
  Repeat,
  SignOut as SignOutIcon,
  Trash as TrashIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import type { Role } from '@/types/role';
import type { Community } from '@/types/v2/community';
import type { Unit } from '@/types/v2/unit';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { useUserTitles } from '@/hooks/use-titles';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';

import { AssignUnitDialog } from './assign-unit-dialog';

/**
 * Renders an "empty" state item with a label and plus button.
 */
function EmptyPropertyItem({ label, onAdd }: { label: string; onAdd?: () => void }): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
      <Typography color="text.secondary" variant="body2">
        None
      </Typography>
      <IconButton aria-label={`Add ${label}`} onClick={onAdd} size="small">
        <PlusCircleIcon />
      </IconButton>
    </Box>
  );
}

function UnitDetail({ unit, residentId }: { unit: Unit; residentId: string }): React.JSX.Element {
  const { community } = useUser();
  const queryClient = useQueryClient();
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = React.useState(false);

  const handleRoleUpdate = async (roleName: string) => {
    try {
      const response = await axios.patch(`/api/v2/communities/${community?.id}/residents/${residentId}/unit-role`, {
        unit_id: unit.id,
        role_name: roleName,
      });

      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ['residents', residentId] });
        toast.success('Unit role updated successfully');
      }
    } catch (error) {
      console.error('Error updating unit role:', error);
      toast.error('Failed to update unit role');
    }
  };

  const handleRemoveUnit = async () => {
    try {
      setIsRemoving(true);
      const response = await axios.delete(`/api/v1/users/${residentId}/units/${unit.id}`);
      
      if (response.status === 200) {
        queryClient.invalidateQueries({ queryKey: ['residents', residentId] });
        queryClient.invalidateQueries({ queryKey: ['residents'] });
        toast.success('User removed from unit successfully');
        setShowRemoveDialog(false);
      }
    } catch (error: any) {
      console.error('Error removing user from unit:', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to remove user from unit';
      toast.error(message);
    } finally {
      setIsRemoving(false);
    }
  };

  const openRemoveDialog = () => {
    setShowRemoveDialog(true);
  };

  // Get the current role name from the unit's role or pivot
  const currentRoleName = unit.role?.name || 'unit_resident';

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.default' }}>
      <Stack spacing={2}>
        {/* Unit Address */}
        <Box>
          <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
            Address
          </Typography>
          <Typography sx={{ fontWeight: 500 }} variant="body2">
            <Link href={`/units/${unit.id}`}>{unit.unit_number ? `Unit #${unit.unit_number}` : unit.address}</Link>
          </Typography>
        </Box>

      

        {/* Unit Role */}
        <Box>
          <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
            Unit Role
          </Typography>
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_OWNER,
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                onChange={(e) => handleRoleUpdate(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
                value={currentRoleName || ''}
              >
                {Object.entries(UNIT_ROLES).map(([key, value]) => (
                  <MenuItem key={value} value={value}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography sx={{ fontWeight: 600, color: value === 'unit_owner' ? 'primary.main' : 'text.primary' }} variant="body2">
                        {UNIT_ROLES_LABELS[value]}
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        {UNIT_ROLES_DESCRIPTIONS[value]}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </RoleGuard>
        </Box>

        {/* Remove Unit Button */}
        <Box>
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_OWNER,
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <Button
              color="error"
              disabled={isRemoving}
              fullWidth
              onClick={openRemoveDialog}
              size="small"
              startIcon={<TrashIcon size={16} />}
              sx={{ 
                textTransform: 'none',
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                }
              }}
              variant="outlined"
            >
              Remove from Unit
            </Button>
          </RoleGuard>
        </Box>
      </Stack>

      {/* Remove Unit Confirmation Dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => { setShowRemoveDialog(false); }}
        open={showRemoveDialog}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Remove User from Unit
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this user from{' '}
            <strong>{unit.unit_number ? `Unit #${unit.unit_number}` : unit.address}</strong>?
            <br /><br />
            This action cannot be undone and will remove all unit-related roles and permissions for this user.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            disabled={isRemoving}
            onClick={() => { setShowRemoveDialog(false); }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={isRemoving}
            onClick={handleRemoveUnit}
            startIcon={isRemoving ? undefined : <TrashIcon size={16} />}
            sx={{ textTransform: 'none' }}
            variant="contained"
          >
            {isRemoving ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Renders the community roles with radio button interface.
 */
function CommunityRoles({
  roles,
  communityTitle,
}: {
  roles?: Role[];
  communityTitle?: string | number | null;
}): React.JSX.Element {
  const { community } = useUser();
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const theme = useTheme();

  // Fetch available user titles
  const { data: userTitles } = useUserTitles();

  const handleRoleUpdate = async (roleName: string) => {
    try {
      if (!community) {
        console.error('Community not found');
        return;
      }
      const response = await axios.post(`/api/v2/communities/${community.id}/residents/${userId}/assign-role`, {
        role: roleName,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update role');
      }
      queryClient.invalidateQueries({ queryKey: ['residents', userId] });
      toast.success('Community role updated successfully');
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update community role');
    }
  };

  const handleTitleUpdate = async (titleId: string | null) => {
    try {
      setIsSavingTitle(true);
      if (!community) {
        console.error('Community not found');
        return;
      }
      console.log('Updating title:', { communityId: community.id, userId, titleId });
      const response = await axios.patch(`/api/v2/communities/${community.id}/residents/${userId}/community-title`, {
        title_id: titleId ? Number(titleId) : null,
      });

      if (response.status !== 200) {
        throw new Error('Failed to update title');
      }
      queryClient.invalidateQueries({ queryKey: ['residents', userId] });
      toast.success('Community title updated successfully');
    } catch (error: any) {
      console.error('Error updating title:', error);
      console.error('Error details:', error.response?.data);
      // revert local selection on error
      setSelectedTitleId(currentTitleId);
      toast.error('Failed to update community title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Get the current role name from the roles array
  const currentRoleName = roles && roles.length > 0 ? roles[0].name : 'community_member';

  // Get the current title ID from the communityTitle prop
  // communityTitle is now the user_title_id directly
  const currentTitleId = React.useMemo(() => {
    if (!communityTitle || !userTitles) return '';
    // If communityTitle is already a number/ID, use it directly
    if (typeof communityTitle === 'number' || !isNaN(Number(communityTitle))) {
      return String(communityTitle);
    }
    // Fallback: if it's still a string title, find the matching title
    const matchingTitle = userTitles.find((title: any) => title.title === communityTitle);
    return matchingTitle ? String(matchingTitle.id) : '';
  }, [communityTitle, userTitles]);

  // Local state to control select to avoid mismatch after sorting
  const [selectedTitleId, setSelectedTitleId] = React.useState<string>(currentTitleId);
  const [isSavingTitle, setIsSavingTitle] = React.useState<boolean>(false);
  React.useEffect(() => {
    if (!isSavingTitle) {
      setSelectedTitleId(currentTitleId);
    }
  }, [currentTitleId, isSavingTitle]);

  // Sort titles by numeric title_id for stable ordering by ID
  const sortedUserTitles = React.useMemo(() => {
    return Array.isArray(userTitles) ? [...userTitles].sort((a: any, b: any) => Number(a.id) - Number(b.id)) : [];
  }, [userTitles]);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Community Roles - Left Column */}
        <Grid item md={6} xs={12}>
          <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
            Community Role
          </Typography>
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_OWNER,
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                onChange={(e) => handleRoleUpdate(e.target.value)}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
                value={currentRoleName || ''}
              >
                <MenuItem value="community_manager">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, color: 'primary.main' }} variant="body2">
                      Community Manager
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      Can manage community settings and residents
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="community_member">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="body2">
                      Community Member
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      Regular community member
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </RoleGuard>
        </Grid>

        {/* Community Titles - Right Column */}
        <Grid item md={6} xs={12}>
          <Typography color="text.secondary" sx={{ display: 'block', mb: 1 }} variant="caption">
            Community Title
          </Typography>
          <RoleGuard
            requiredAccountRoles={[
              ACCOUNT_ROLES.ACCOUNT_OWNER,
              ACCOUNT_ROLES.ACCOUNT_ADMIN,
              ACCOUNT_ROLES.ACCOUNT_MANAGER,
            ]}
            requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
          >
            <FormControl fullWidth size="small">
              <Select
                displayEmpty
                onChange={(e) => {
                  const v = (e.target.value) || '';
                  setSelectedTitleId(v);
                  void handleTitleUpdate(v || null);
                }}
                sx={{
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  },
                }}
                value={selectedTitleId}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary' }} variant="body2">
                      No Title
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      No specific title assigned
                    </Typography>
                  </Box>
                </MenuItem>
                {sortedUserTitles.map((title: any) => (
                  <MenuItem key={title.id} value={String(title.id)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="body2">
                        {title.title}
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        {title.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </RoleGuard>
        </Grid>
      </Grid>
    </Box>
  );
}

interface CommunityAndUnitDetailsProps {
  community: Community;
  units: Unit[];
  communityRoles: Role[];
  communityTitle?: string | number | null;
  loading?: boolean;
}

/**
 * Main component for rendering unit and community details using reusable sub-components.
 */
export function CommunityAndUnitDetails({
  community,
  units,
  communityRoles,
  communityTitle,
  loading = false,
}: CommunityAndUnitDetailsProps): React.JSX.Element {
  const { userId } = useParams<{ userId: string }>();
  const [assignUnitDialogOpen, setAssignUnitDialogOpen] = React.useState(false);

  // Check if the user already has the "Community Manager" role
  const hasCommunityManagerRole = communityRoles.some((role) => role.name === COMMUNITY_ROLES.COMMUNITY_MANAGER);

  const unitItems = loading
    ? Array.from(new Array(3)).map((_, index) => ({
        key: index.toString(),
        label: <Skeleton variant="text" width={100} />,
        value: <Skeleton height={50} variant="rectangular" />,
      }))
    : units.length > 0
      ? units.map((unit) => {
          const isCurrentUnit = unit.pivot.move_out_date === null;
          return {
            key: unit.id.toString(),
            label: <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} />,
            value: <UnitDetail residentId={userId!} unit={unit} />,
          };
        })
      : [
          {
            key: 'Unit',
            label: 'Unit',
            value: <EmptyPropertyItem label="Unit" onAdd={() => { setAssignUnitDialogOpen(true); }} />,
          },
        ];

  const communityItems: { key: string; value: React.ReactNode }[] = loading
    ? [
        { key: 'Community', value: <Skeleton variant="text" width={150} /> },
        { key: 'Roles', value: <Skeleton height={50} variant="rectangular" /> },
      ]
    : [
        { key: 'Community', value: community.name },
        {
          key: 'Roles',
          value: <CommunityRoles communityTitle={communityTitle} roles={communityRoles} />,
        },
      ];

  return (
    <Stack spacing={4}>
      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <HouseIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Unit Details"
        />
        <Box sx={{ maxHeight: '460px', overflowY: 'auto' }}>
          <PropertyList divider={<Divider />} orientation="vertical" sx={{ '--PropertyItem-padding': '12px 24px' }}>
            {unitItems.map((item) => (
              <PropertyItem key={item.key} name={item.label} value={item.value} />
            ))}
          </PropertyList>
        </Box>
      </Card>

      {/* Assign Unit Dialog */}
      <AssignUnitDialog
        onClose={() => { setAssignUnitDialogOpen(false); }}
        onSuccess={() => { setAssignUnitDialogOpen(false); }}
        open={assignUnitDialogOpen}
        userId={userId!}
      />

      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <HouseIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Community Details"
        />
        <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
          <PropertyList divider={<Divider />} orientation="vertical" sx={{ '--PropertyItem-padding': '12px 24px' }}>
            {communityItems.map((item) => (
              <PropertyItem key={item.key} name={item.key} value={item.value} />
            ))}
          </PropertyList>
        </Box>
      </Card>
    </Stack>
  );
}
